import { Icon, IconName, Tooltip } from '@usealloy/component-library';
import React from 'react';
import styled from 'styled-components';

import { truncateText } from './utils/mixins';
import { SidebarButtonBase } from './SidebarButtonBase';
import { useSidebarCtx } from './SidebarContext';

// ============================================================
//     SIDEBAR MENU - ITEM
// ============================================================
const StyledItem = styled.li({
  position: 'relative',
  listStyle: 'none',

  // DAFFY SASS OVERRIDES
  '&&& a': {
    margin: 0,
    textTransform: 'none',
  },

  '& svg': {
    flexShrink: 0,
  },
});

/**
 * Semantic container for a single menu item within a `Sidebar.Menu`.
 *
 * Renders as an `<li>` element and provides relative positioning context for child elements like `MenuAction` and `MenuBadge`.
 *
 * @example
 * ```tsx
 * <Sidebar.MenuItem>
 *   <Sidebar.MenuButton tooltip="Home">
 *     <Icon icon={IconName.Home} size="small" />
 *     <span>Home</span>
 *   </Sidebar.MenuButton>
 * </Sidebar.MenuItem>
 * ```
 */
export const MenuItem = React.forwardRef<
HTMLLIElement,
React.ComponentPropsWithoutRef<'li'>
>((props, forwardedRef) => (
  <StyledItem {...props} ref={forwardedRef} data-component="Sidebar.MenuItem" />
));

MenuItem.displayName = 'Sidebar.MenuItem' as const;

// ============================================================
//     SIDEBAR MENU - BUTTON
// ============================================================
interface MenuButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  /**
   * Render as a different element or component via styled-components polymorphism.
   *
   * Use for router links (`as={Link}` with `to`) or full-page loads (`as="a"` with `href`).
   */
  as?: React.ElementType;
  /**
   * The destination for `as="a"` rendering
   */
  href?: string;
  /**
   * Apply `active` styles to the button, indicating it is the current page or item
   */
  isActive?: boolean;
  /**
   * The destination for `as={Link}` rendering
   */
  to?: string;
  /**
   * Optional tooltip to display when the button is hovered
   *
   * NOTE: Tooltip only appears when the sidebar is in a collapsed state
   */
  tooltip?: React.ReactNode;
  /**
   * Positioning of the rendered tooltip: 'top', 'right', 'bottom', or 'left'
   */
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
}

const StyledButton = styled(SidebarButtonBase)({
  justifyContent: 'flex-start',
  gap: 'var(--cl-space-3)',

  minWidth: 0,
  // border: "none",
  overflow: 'clip',

  '--btn-bg': 'transparent',
  '--btn-bg-hover': 'var(--cl-gray-200)',
  '--btn-bg-active': 'var(--cl-gray-300)',

  fontWeight: 'var(--cl-font-weight-regular)',
  borderRadius: 'var(--cl-radius-1)',

  '&[data-active="true"], &.active': {
    backgroundColor: 'var(--btn-bg-active)',
    fontWeight: 'var(--cl-font-weight-bold)',
  },

  '& > span': {
    ...truncateText,
  },

  // Selectors for when the MenuButton has a MenuAction sibling
  '&:has(~ [data-component="Sidebar.MenuAction"])': {
    // paddingRight: "var(--cl-space-8)",
  },
});

/**
 * Clickable navigation button for a single menu item within a `Sidebar.MenuItem`.
 *
 * Supports active state indication and optional tooltips (shown only when sidebar is collapsed).
 * Can render as a custom element via the `as` prop for router `<Link>` components or plain anchors.
 *
 * NOTE: Wrap text content in a `<span>` for proper text truncation when the sidebar is collapsed.
 *
 * #### Important Props:
 * - `isActive` - highlights the button as the current/active item
 * - `tooltip` - content shown in tooltip when sidebar is collapsed
 * - `as` - render as a custom element (e.g. `as={Link}` with `to`, or `as="a"` with `href`)
 *
 * @example
 * ```tsx
 * <Sidebar.MenuItem>
 *   <Sidebar.MenuButton tooltip="Home" as={Link} to="/home">
 *     <Icon icon={IconName.Home} size="small" />
 *     <span>Home</span>
 *   </Sidebar.MenuButton>
 * </Sidebar.MenuItem>
 * ```
 */
export const MenuButton = React.forwardRef<HTMLButtonElement, MenuButtonProps>(
  (props, forwardedRef) => {
    const {
      as,
      isActive,
      tooltip,
      tooltipSide = 'right',
      type,
      ...restProps
    } = props;
    const { isExpanded, isMobile } = useSidebarCtx();

    const btn = (
      <StyledButton
        // Only real <button> elements should get a default type attribute
        type={as ? type : type ?? 'button'}
        {...restProps}
        as={as}
        ref={forwardedRef}
        data-active={isActive}
        data-component="Sidebar.MenuButton"
        data-size="medium"
        data-stretch=""
      />
    );

    if (!tooltip) {
      return btn;
    }

    return (
      <Tooltip
        content={tooltip}
        disabled={isExpanded || isMobile}
        side={tooltipSide}
      >
        {btn}
      </Tooltip>
    );
  },
);

MenuButton.displayName = 'Sidebar.MenuButton' as const;

// ============================================================
//     SIDEBAR MENU - ACTION
// ============================================================
interface MenuActionProps
  extends Omit<React.ComponentPropsWithoutRef<'button'>, 'children'> {
  /** Required for accessibility */
  label: string;
}

const StyledAction = styled(SidebarButtonBase)({
  position: 'absolute',
  top: 'var(--cl-space-2)',
  right: 'var(--cl-space-2)',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',

  '--btn-bg': 'transparent',
  '--btn-bg-hover': 'var(--cl-background-secondary-hovered)',

  '&[data-expanded="false"]': {
    display: 'none',
  },
});

/**
 * Secondary action button positioned absolutely within a `Sidebar.MenuItem`.
 *
 * Renders an ellipsis icon button for additional actions (e.g., dropdown menus).
 * Hidden when the sidebar is collapsed.
 *
 * #### Important Props:
 * - `label` - required accessible label describing the action
 *
 * @example
 * ```tsx
 * <Sidebar.MenuItem>
 *   <Sidebar.MenuButton tooltip="Home">
 *     <Icon icon={IconName.Home} size="small" />
 *     <span>Home</span>
 *   </Sidebar.MenuButton>
 *   <DropdownMenu>
 *     ...with <Sidebar.MenuAction label="More options" /> as the trigger
 *   </DropdownMenu>
 * </Sidebar.MenuItem>
 * ```
 */
export const MenuAction = React.forwardRef<HTMLButtonElement, MenuActionProps>(
  (props, forwardedRef) => {
    const { label, ...restProps } = props;
    const { isExpanded } = useSidebarCtx();
    return (
      <StyledAction
        type="button"
        {...restProps}
        ref={forwardedRef}
        aria-label={label}
        data-component="Sidebar.MenuAction"
        data-expanded={isExpanded}
        data-icon-only=""
        data-size="xsmall"
      >
        <Icon icon={IconName.BoldVerticalEllipsis} size="small" />
      </StyledAction>
    );
  },
);

MenuAction.displayName = 'Sidebar.MenuAction' as const;

// ============================================================
//     SIDEBAR MENU - BADGE
// ============================================================
interface MenuBadgeProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  /**
   * The value to display in the badge
   *
   * If the value is greater than 99, it will be displayed as '99+'
   */
  value?: number;
}

const StyledBadge = styled.div({
  position: 'absolute',
  top: 'var(--cl-space-2)',
  right: 'var(--cl-space-2)',

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 'var(--cl-space-6)',
  minWidth: 'var(--cl-space-6)',
  padding: 'var(--cl-space-1)',
  borderRadius: 'var(--cl-radius-circle)',
  pointerEvents: 'none',

  backgroundColor: 'var(--cl-background-attention)',
  fontSize: 'var(--cl-font-size-1)',
  fontWeight: 'var(--cl-font-weight-bold)',
  userSelect: 'none',

  transitionProperty: 'top, right, height, width, color, background-color',
  transitionDuration: 'var(--cl-duration-300)',
  overflow: 'hidden',

  '&[data-expanded="false"]': {
    top: 0,
    right: 0,
    height: 'var(--cl-space-2)',
    width: 'var(--cl-space-2)',
    minWidth: 'var(--cl-space-2)',
    transform: 'translate(25%, -25%)',
    color: 'transparent',
  },
});

/**
 * Notification badge indicator for a single menu item within a `Sidebar.MenuItem`.
 *
 * Displays a numeric value when expanded (capped at "99+") and collapses to a small dot indicator when the sidebar is collapsed.
 *
 * #### Important Props:
 * - `value` - numeric count to display (values > 99 show as "99+")
 *
 * @example
 * ```tsx
 * <Sidebar.MenuItem>
 *   <Sidebar.MenuButton tooltip="Home">
 *     <Icon icon={IconName.Home} size="small" />
 *     <span>Home</span>
 *   </Sidebar.MenuButton>
 *   <Sidebar.MenuBadge value={3} />
 * </Sidebar.MenuItem>
 * ```
 */
export const MenuBadge = React.forwardRef<HTMLDivElement, MenuBadgeProps>(
  (props, forwardedRef) => {
    const { value, ...restProps } = props;
    const { isExpanded } = useSidebarCtx();

    const formattedValue = value && value > 99 ? '99+' : String(value);

    return (
      <StyledBadge
        {...restProps}
        ref={forwardedRef}
        data-component="Sidebar.MenuBadge"
        data-expanded={isExpanded}
      >
        {formattedValue}
      </StyledBadge>
    );
  },
);

MenuBadge.displayName = 'Sidebar.MenuBadge' as const;
